import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Spinner from "../../../../../Components/common/Spinner/Spinner";
import TabButton from "../../../../../Components/common/TabButton/TabButton";
import Warning from "../../../../../Components/common/Warning/Warning";
import HeaderStatusToggle from "../../Components/HeaderStatusToggle";
import Notify from "./Tabs/Notify";
import Proxy from "./Tabs/Proxy";
import LLM from "./Tabs/LLM";
import General from "./Tabs/General";
import {
  INITIAL_LLM_CONFIGS,
  llmConfigsFromDB,
  providerChangeConfigs,
  validateLlmConfigs,
  buildLlmRequestBody,
} from "../LLM/llmConfig";

import {
  RequestGetConfig,
  RequestSetConfig,
  RequestResetConfig,
  RequestTogglePhishboxLlm,
} from "../../../../../store/actions/integrationsActions";
import {
  RequestTestLlmSettings,
  ClearLlmTestResult,
} from "../../../../../store/actions/settingsActions";

import "./styles.scss";

// Tab label -> the section key the backend resets (appdeployer.py phishbox 'tabs').
const TAB_SECTIONS = {
  Notify: "notify",
  Proxy: "proxy",
  LLM: "llm",
  General: "general",
};

const PROXY_STRICT_REGEX = /^[a-zA-Z0-9.-]+:\d+$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// The backend returns "*****" as the custom LLM api key when one is saved
const MASKED_SECRET = "*****";

const toNumberOrNull = (value) => {
  const trimmed = String(value ?? "").trim();
  return trimmed === "" ? null : Number(trimmed);
};

// custom_llm form state -> stored shape (numbers as numbers, unset values as null)
const customLlmPayload = (customLlm) => {
  const body = buildLlmRequestBody(customLlm);
  return {
    ...body,
    temperature: toNumberOrNull(body.temperature),
    max_tokens: toNumberOrNull(body.max_tokens),
    timeout: toNumberOrNull(body.timeout),
  };
};

// LLM fields of the old LLM tab, replaced by custom_llm. Older stored configs may
// still carry them; they are dropped so they are not saved again.
const LEGACY_LLM_KEYS = ["default_llm", "external_llm", "llm_key", "llm_timeout"];

// Stored phishbox config -> form state. custom_llm uses the LLM settings form shape.
const configsFromDB = (config) => {
  const cleaned = { ...config };
  LEGACY_LLM_KEYS.forEach((key) => delete cleaned[key]);
  return { ...cleaned, custom_llm: llmConfigsFromDB(config.custom_llm) };
};

const convertToNumber = (data) => {
  const converted = { ...data };

  // uservalid, ssl_proxy (true -> 1, false -> 0)
  if (converted.uservalid !== undefined) {
    converted.uservalid = converted.uservalid ? 1 : 0;
  }
  if (converted.ssl_proxy !== undefined) {
    converted.ssl_proxy = converted.ssl_proxy ? 1 : 0;
  }

  if (converted.bulk !== undefined && converted.bulk !== "") {
    converted.bulk = Number(converted.bulk);
  }

  // notify.tls (true -> 1, false -> 0)
  if (converted.notify && converted.notify.tls !== undefined) {
    converted.notify.tls = converted.notify.tls ? 1 : 0;
  }

  // notify.enabled (true -> 1, false -> 0)
  if (converted.notify && converted.notify.enabled !== undefined) {
    converted.notify.enabled = converted.notify.enabled ? 1 : 0;
  } else {
    converted.notify.enabled = 0; // Eğer notify.enabled yoksa varsayılan olarak 0 yap
  }

  // TIMEOUT DEĞERLERİNİ NUMBER YAP
  if (converted.timeout !== undefined && converted.timeout !== "") {
    converted.timeout = Number(converted.timeout);
  }

  return converted;
};

// Nesnelerin içeriğini derinlemesine karşılaştırır
const areConfigsEqual = (obj1, obj2) => {
  return JSON.stringify(obj1) === JSON.stringify(obj2);
};

const INITIAL_CONFIGS = {
  notify: {
    server: "",
    port: "",
    tls: null,
    username: "",
    password: "",
    company: "",
    short: "",
    cirtemail: "",
    enabled: null,
  },
  bulk: "",
  http_proxy: "",
  https_proxy: "",
  username: "", // Proxy user
  password: "", // Proxy pass
  timeout: "",
  uservalid: null,
  ssl_proxy: null,
  custom_llm: INITIAL_LLM_CONFIGS,
};

const PhishboxSettings = () => {
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState("Notify");
  const [configs, setConfigs] = useState(INITIAL_CONFIGS);
  const [errors, setErrors] = useState({});
  const [showResetWarning, setShowResetWarning] = useState(false);

  const isLoading = useSelector((state) => state.integrations.loading);
  const resetLoading = useSelector((state) => state.integrations.resetConfigLoading);
  const toggleLlmLoading = useSelector((state) => state.integrations.togglePhishboxLlmLoading);
  const configFromDB = useSelector((state) => state.integrations.getConfig);
  const testLoading = useSelector((state) => state.settings.llmTestLoading);
  const testResult = useSelector((state) => state.settings.llmTestResult);

  useEffect(() => {
    dispatch(RequestGetConfig("phishbox"));
    dispatch(ClearLlmTestResult());
  }, [dispatch]);

  const savedConfigs = useMemo(
    () => (configFromDB && configFromDB.config ? configsFromDB(configFromDB.config) : null),
    [configFromDB],
  );
  const savedCustomLlm = configFromDB?.config?.custom_llm;
  // The stored key is reused only while the provider stays the same
  const hasStoredKey =
    savedCustomLlm?.api_key === MASKED_SECRET &&
    savedCustomLlm?.provider === configs.custom_llm.provider;

  useEffect(() => {
    if (savedConfigs) {
      setConfigs(savedConfigs);
    }
  }, [savedConfigs]);

  const customLlmChangeHandler = (changes) => {
    setConfigs((prev) => ({ ...prev, custom_llm: { ...prev.custom_llm, ...changes } }));
    if (testResult) dispatch(ClearLlmTestResult());
  };

  // Same as the LLM settings page: disabling a saved LLM is saved right away, enabling
  // only unlocks the form and goes live after Save validates and tests it.
  const llmToggleHandler = (active) => {
    if (active) {
      customLlmChangeHandler({ active: true });
      return;
    }
    setErrors({});
    if (savedConfigs && savedConfigs.custom_llm.active) {
      dispatch(RequestTogglePhishboxLlm({ active: false }));
    } else {
      setConfigs((prev) => ({
        ...prev,
        custom_llm: savedConfigs ? savedConfigs.custom_llm : INITIAL_LLM_CONFIGS,
      }));
      if (testResult) dispatch(ClearLlmTestResult());
    }
  };

  const customLlmProviderChangeHandler = (item) => {
    customLlmChangeHandler(
      providerChangeConfigs(
        item.id,
        configs.custom_llm,
        savedConfigs ? savedConfigs.custom_llm : INITIAL_LLM_CONFIGS,
      ),
    );
  };

  const inputChangeHandler = (e) => {
    const { name, value, type } = e.target;

    // Radio Buttons logic (uservalid buraya düşecek)
    if (type === "radio") {
      const intValue = Number(value);
      if (name.startsWith("notify.")) {
        const key = name.split(".")[1];
        setConfigs((prev) => ({
          ...prev,
          notify: { ...prev.notify, [key]: intValue },
        }));
      } else {
        setConfigs((prev) => ({ ...prev, [name]: intValue }));
      }
      return;
    }

    if (type === "number" && name !== "notify.port") {
      const intValue = Number(value);
      setConfigs((prev) => ({ ...prev, [name]: intValue }));
      return;
    }

    // Normal Inputs logic (number inputlar buraya düşer, value string gelir ama sorun değil)
    if (name.startsWith("notify.")) {
      const key = name.split(".")[1];
      setConfigs((prev) => ({
        ...prev,
        notify: { ...prev.notify, [key]: value },
      }));
    } else {
      setConfigs((prev) => ({ ...prev, [name]: value }));
    }
  };

  const filterEmptyValues = (data) => {
    const cleaned = {};
    for (const key in data) {
      const value = data[key];

      if (value === null) {
        continue;
      }

      if (typeof value === "object" && !Array.isArray(value)) {
        const nestedCleaned = filterEmptyValues(value);
        if (Object.keys(nestedCleaned).length > 0) {
          cleaned[key] = nestedCleaned;
        }
        continue;
      }

      if (typeof value === "string" && value.trim() === "") {
        continue;
      }

      cleaned[key] = value;
    }
    return cleaned;
  };

  const validateConfigs = (currentConfigs) => {
    const newErrors = {};
    const notifyFields = currentConfigs.notify;

    // Zorunlu alan kontrolü (Notify içindekiler + Proxy alanları)
    const requiredFields = [
      { key: "notify.server", value: notifyFields.server, label: "Notify Server" },
      { key: "notify.port", value: notifyFields.port, label: "Notify Port" },
      { key: "notify.username", value: notifyFields.username, label: "Notify Username" },
      { key: "notify.password", value: notifyFields.password, label: "Notify Password" },
      { key: "notify.company", value: notifyFields.company, label: "Notify Company" },
      { key: "notify.short", value: notifyFields.short, label: "Notify Subject" },
      { key: "notify.cirtemail", value: notifyFields.cirtemail, label: "CIRT Email" },
      { key: "http_proxy", value: currentConfigs.http_proxy, label: "HTTP Proxy" },
      { key: "https_proxy", value: currentConfigs.https_proxy, label: "HTTPS Proxy" },
      { key: "password", value: currentConfigs.password, label: "Password" },
    ];

    requiredFields.forEach((field) => {
      if (typeof field.value === "string" && field.value.trim() === "") {
        newErrors[field.key] = `${field.label} is a required field.`;
      }
    });

    // Format Kontrolü (Ekstra Validasyon)

    // Port sadece sayı olmalı (Port input type number olmasına rağmen value string döner)
    if (notifyFields.port && isNaN(Number(notifyFields.port))) {
      newErrors["notify.port"] = "Enter a valid port number.";
    }

    // CIRT Email kontrolü
    if (notifyFields.cirtemail && !EMAIL_REGEX.test(notifyFields.cirtemail.trim())) {
      newErrors["notify.cirtemail"] = "Enter a valid email address.";
    }

    // HTTP Proxy kontrolü
    if (currentConfigs.http_proxy) {
      const val = currentConfigs.http_proxy.trim();
      if (!PROXY_STRICT_REGEX.test(val)) {
        newErrors["http_proxy"] =
          'The format is incorrect! Please enter it in the "host:port" format (e.g. 10.10.1.1:8080 or proxy.local:8080). Do not include the protocol (http://).';
      }
    }

    // HTTPS Proxy kontrolü
    if (currentConfigs.https_proxy) {
      const val = currentConfigs.https_proxy.trim();
      if (!PROXY_STRICT_REGEX.test(val)) {
        newErrors["https_proxy"] =
          'The format is incorrect! Please enter it in the "host:port" format (e.g. 10.10.1.1:8080 or proxy.local:8080). Do not include the protocol (https://).';
      }
    }

    // Custom LLM: connection fields only have to be complete when it is enabled
    const customLlmErrors = validateLlmConfigs(currentConfigs.custom_llm, {
      requireAll: currentConfigs.custom_llm.active,
      hasStoredKey,
    });
    Object.keys(customLlmErrors).forEach((key) => {
      newErrors[`custom_llm.${key}`] = customLlmErrors[key];
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // Hata yoksa true döner
  };

  const checkSaveButtonDisabled = () => {
    // 1. Zorunlu Alan Kontrolü (Notify alanları + Proxyler)
    const notifyFields = configs.notify;
    const requiredFieldsFilled =
      notifyFields.server?.trim() !== "" &&
      notifyFields.port?.toString().trim() !== "" &&
      notifyFields.username?.trim() !== "" &&
      notifyFields.password?.trim() !== "" &&
      notifyFields.company?.trim() !== "" &&
      notifyFields.short?.trim() !== "" &&
      notifyFields.cirtemail?.trim() !== "" &&
      configs.http_proxy?.trim() !== "" &&
      configs.https_proxy?.trim() !== "" &&
      configs.password?.trim() !== "";

    // 2. Değişiklik Kontrolü
    // Eğer DB'den veri henüz gelmemişse INITIAL_CONFIGS ile karşılaştır, gelmişse DB verisiyle.
    const baseConfig = savedConfigs || INITIAL_CONFIGS;
    const isDataChanged = !areConfigsEqual(configs, baseConfig);

    // Butonun DISABLE olma durumu:
    // Zorunlu alanlar dolmamışsa VEYA hiçbir veri değişmemişse buton kapalı kalsın.
    return !requiredFieldsFilled || !isDataChanged;
  };

  const saveHandler = async () => {
    const isValid = validateConfigs(configs);

    if (!isValid) {
      return;
    }

    // An empty api key is dropped here, the backend then keeps the stored one
    const filteredConfigs = filterEmptyValues({
      ...configs,
      custom_llm: customLlmPayload(configs.custom_llm),
    });
    const finalConfigs = convertToNumber(filteredConfigs);

    dispatch(RequestSetConfig({ appname: "phishbox", config: JSON.stringify(finalConfigs) }));
  };

  // Same test as the LLM settings page; an empty key is tested with the key saved
  // in the phishbox config (source: "phishbox").
  const testLlmHandler = () => {
    const llmErrors = validateLlmConfigs(configs.custom_llm, { requireAll: true, hasStoredKey });
    const newErrors = {};
    Object.keys(llmErrors).forEach((key) => {
      newErrors[`custom_llm.${key}`] = llmErrors[key];
    });
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;
    dispatch(
      RequestTestLlmSettings({ ...buildLlmRequestBody(configs.custom_llm), source: "phishbox" }),
    );
  };

  // Only the fields of the active tab go back to their defaults; the backend keeps
  // the other tabs untouched and the refreshed config flows back into `configs`.
  const resetHandler = () => {
    setShowResetWarning(false);
    setErrors({});
    dispatch(RequestResetConfig({ appname: "phishbox", section: TAB_SECTIONS[activeTab] }));
  };

  const isLlmTab = activeTab === "LLM";
  // Enabled in the form but not saved yet; Save tests the connection first
  const isPendingActivation =
    configs.custom_llm.active && !(savedConfigs && savedConfigs.custom_llm.active);

  return (
    <div className="phishbox-settings">
      <div className="phishbox-settings__header">
        <h1>Phishbox</h1>
      </div>

      <div className="phishbox-settings__body">
        <div className="phishbox-settings__body__header">
          <h3>{activeTab}</h3>
          {isLlmTab ? (
            <>
              <HeaderStatusToggle
                active={configs.custom_llm.active}
                onClick={(active) => !toggleLlmLoading && llmToggleHandler(active)}
              />
              <p>
                NOTE: When enabled, phishing checks use the LLM configured below. When disabled, the
                built-in CyberFortress AI is used.
              </p>
            </>
          ) : (
            <p>Configure {activeTab} settings below.</p>
          )}
        </div>

        <div className="button-block">
          <TabButton title="Notify" onClick={setActiveTab} active={activeTab} />
          <TabButton title="Proxy" onClick={setActiveTab} active={activeTab} />
          <TabButton title="LLM" onClick={setActiveTab} active={activeTab} />
          <TabButton title="General" onClick={setActiveTab} active={activeTab} />
        </div>

        {/* Tab Content Area */}
        <div className="phishbox-settings__body__content">
          {activeTab === "Notify" && <Notify configs={configs} onChange={inputChangeHandler} />}
          {activeTab === "Proxy" && <Proxy configs={configs} onChange={inputChangeHandler} />}
          {activeTab === "LLM" && (
            <LLM
              configs={configs}
              onCustomLlmChange={customLlmChangeHandler}
              onCustomLlmProviderChange={customLlmProviderChangeHandler}
              hasStoredKey={hasStoredKey}
            />
          )}
          {activeTab === "General" && <General configs={configs} onChange={inputChangeHandler} />}

          {/* Reset (active tab only) + Global Save Button */}
          <div className="phishbox-settings__actions">
            {isLlmTab && (
              <button
                className="m-r-15 default-button"
                onClick={testLlmHandler}
                disabled={testLoading || isLoading || !configs.custom_llm.active}>
                {testLoading ? <Spinner width={12} height={12} /> : "Test Connection"}
              </button>
            )}
            <button
              className="m-r-15 default-button"
              onClick={() => setShowResetWarning(true)}
              disabled={isLoading || resetLoading}>
              {resetLoading ? <Spinner width={12} height={12} /> : "Reset"}
            </button>
            <button
              className="primary-button"
              onClick={saveHandler}
              disabled={isLoading || checkSaveButtonDisabled()}>
              {isLoading ? <Spinner width={12} height={12} /> : "Save"}
            </button>
          </div>

          {isLlmTab && isPendingActivation && (
            <p className="llm-settings__hint">
              Not saved yet. Click Save to activate the LLM. The connection is tested before the
              settings are saved.
            </p>
          )}

          {isLlmTab && testResult && (
            <p
              className={`llm-settings__test-result ${
                testResult.success ? "llm-settings__test-result--success" : ""
              }`}>
              {testResult.message}
              {testResult.latency_ms !== undefined && ` (${testResult.latency_ms} ms)`}
            </p>
          )}

          {Object.keys(errors).length > 0 ? (
            <div>
              {Object.keys(errors).map((key, i) => (
                <p key={i} style={{ color: "red", marginTop: "10px" }}>
                  {key}: {errors[key]}
                </p>
              ))}
            </div>
          ) : null}
        </div>
      </div>
      <Warning
        onClickOk={resetHandler}
        show={showResetWarning}
        setShow={setShowResetWarning}
        text={`The ${activeTab} settings will be reset to their default values. The other tabs will stay as they are. Do you want to continue?`}
        article="Reset"
      />
    </div>
  );
};

export default PhishboxSettings;
